import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserOrganization } from "@/hooks/useUserOrganization";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import type { BulkUploadConfig, BulkUploadColumn } from "@/components/bulk-upload/BulkUploadDialog";
import * as XLSX from "xlsx";

// ─── Payroll ───────────────────────────────────────
// Columns match the user's Excel file layout.
// basic_salary is DERIVED from PF (see onUpload) — never read from an annual CTC column.
const payrollColumns: BulkUploadColumn[] = [
  // Employee identification
  {
    key: "employee_id",
    label: "Employee Name",
    required: true,
    aliases: ["employee_name", "emp_name", "name", "employee"],
  },
  {
    key: "email_id",
    label: "Email ID",
    aliases: ["email", "email_address", "emp_email", "employee_email", "login_email"],
  },

  // Monthly salary inputs (MONTHLY figures only — do NOT use annual CTC columns here)
  {
    key: "monthly_gross",
    label: "Monthly Fixed Salary",
    required: true,
    aliases: [
      "monthly_fixed_salary",   // "Monthly fixed Salary" (standard)
      "montly_fixed_salary",    // common typo in Excel (missing 'h')
      "monthly_fixed_sala",     // truncated display variant
      "montly_fixed_sala",      // truncated + typo variant
      "fixed_salary",
      "monthly_salary",
      "fixed_gross",
    ],
  },
  {
    key: "gross_earnings_monthly",
    label: "Gross Earnings",
    aliases: [
      "gross_earnings",
      "gross_earn",             // truncated display variant
      "total_earnings",
      "monthly_gross_earnings",
      "total_gross",
    ],
  },

  // Per-component earnings split (used by Bulk Edit on the Register tab).
  // When present, the breakdown is built component-by-component instead of
  // collapsing everything into a single "Basic Salary" line at gross.
  {
    key: "basic_monthly",
    label: "Basic Salary",
    aliases: ["basic", "basic_pay", "basic_salary", "basic_salary_monthly"],
  },
  {
    key: "hra_monthly",
    label: "HRA",
    aliases: ["hra", "house_rent_allowance", "hra_monthly_amount"],
  },
  {
    key: "transport_monthly",
    label: "Transport",
    aliases: ["transport", "transport_allowance", "conveyance", "conveyance_allowance"],
  },
  {
    key: "other_allowances_monthly",
    label: "Other Allowances",
    aliases: ["other_allowances", "other_allow", "misc_allowance", "miscellaneous_allowance"],
  },

  // Statutory deductions — monthly amounts as deducted from payslip
  // Note: "PF- optout" is automatically treated as 0 (basic derived from 40% of gross instead)
  {
    key: "pf_employee_monthly",
    label: "Employee PF Deduction (Monthly)",
    aliases: [
      "employee_pf_deduction_monthly",
      "employee_pf_deduction_me",  // truncated display variant
      "employee_pf_monthly",
      "pf_deduction_monthly",
      "pf_monthly",
      "epf_monthly",
      "employee_pf",
      "pf_employee",
    ],
  },
  {
    key: "professional_tax_monthly",
    label: "Profession Tax Monthly",
    aliases: [
      "profession_tax_monthly",
      "profession_tax_me",         // truncated display variant
      "professional_tax_monthly",
      "pt_monthly",
      "professional_tax",
      "profession_tax",
      "tax_monthly",
      "prof_tax",
    ],
  },
  // TDS (income tax deducted at source) — monthly amount
  {
    key: "tds_monthly",
    label: "TDS (Monthly)",
    aliases: [
      "tds",
      "tds_monthly",
      "income_tax_monthly",
      "tax_deduction_monthly",
      "income_tax",
      "tds_amount",
    ],
  },
  // Other / miscellaneous deductions not covered by PF, PT, TDS or LOP
  // (salary advances recovered, welfare fund, canteen, etc.)
  {
    key: "other_deductions_col",
    label: "Other Deductions",
    aliases: [
      "other_deductions",
      "other_ded",
      "misc_deductions",
      "miscellaneous_deductions",
      "other deductions",
      "other_deduction",
    ],
  },
  // "Total Deductions" is present in the template so users can paste data directly.
  // When the individual PF/PT columns are absent (or zero), this total is used to
  // derive statutory components via back-calculation.
  {
    key: "total_deductions_col",
    label: "Total Deductions",
    aliases: [
      "total_deductions",
      "deductions",
      "total_deduction",
      "deduction_total",
    ],
  },

  // Variable pay — "no" values are safely treated as 0
  {
    key: "incentive_monthly",
    label: "Incentive Monthly",
    aliases: ["incentive_monthly", "incentive_mon", "monthly_incentive", "variable_pay", "incentive"],
  },
  {
    key: "bonus_monthly",
    label: "Bonus Monthly",
    aliases: ["bonus_monthly", "bonus_mon", "monthly_bonus", "bonus"],
  },

  // Attendance / Loss of Pay
  {
    key: "working_days_col",
    label: "Working Days",
    aliases: ["working_days", "total_working_days", "work_days"],
  },
  {
    key: "paid_days_col",
    label: "Paid Days",
    aliases: ["paid_days", "actual_paid_days", "total_paid_days"],
  },
  {
    key: "lwp_days_col",
    label: "LWP Days",
    aliases: ["lwp_days", "lop_days", "loss_of_pay_days", "lwp_day"],
  },
  {
    key: "lwp_deduction_col",
    label: "LWP Deduction",
    aliases: ["lwp_deduction", "lop_deduction", "loss_of_pay_deduction", "lwp_amount"],
  },

  // Take-home
  {
    key: "net_pay_file",
    label: "Net Pay",
    aliases: ["net_pay", "take_home", "take_home_pay", "net_payable", "net_salary"],
  },
];

// Template column names mirror this company's exact Excel file headers.
// Columns like Department, Job Title, Total Annual CTC, Annual CTC, Employer PF Annual,
// Bonus Yearly, and Total Deductions are included so users can paste their data directly —
// they are present in the template but ignored during upload (not mapped in payrollColumns).
// "PF- optout" in the PF column is handled gracefully (basic derived from 40% of gross).
// "no" in Incentive/Bonus columns is treated as 0.
const payrollTemplate = `Employee Name,Email ID,Department,Job Title,Total Annual CTC,Annual CTC,Employer PF Annual,Bonus Yearly,Incentive monthly,Bonus monthly,Monthly fixed Salary,Basic Salary,HRA,Transport,Other Allowances,Gross Earnings,Profession Tax monthly,Employee PF deduction monthly,TDS,Other Deductions,Total Deductions,LWP Days,LWP Deduction,Working Days,Paid Days,Net Pay
Ravi Kumar,ravi@company.com,Engineering,Developer,564000,564000,21600,no,2000,no,45000,22500,11250,2000,9250,47000,200,1800,0,0,2000,0,,26,26,45000
Priya Sharma,priya@company.com,HR,HR Manager,360000,360000,18720,no,no,no,30000,15000,7500,1500,6000,30000,200,1560,500,0,3414,1,1154,26,25,26586
Dilli Ram Nirola,admin@grx10.com,Management,Director,540000,540000,PF- optout,no,no,no,45000,22500,11250,2000,9250,45000,200,0,1500,0,1700,0,,31,31,43300`;

// ─── Attendance ────────────────────────────────────
// Column A is the employee NAME (matches the XLSX template's dropdown).
// `employee_id` is kept as an alias so older templates that still have
// the numeric-id column header upload without re-downloading.
// leave_type required when status=leave|half_day — resolved by name on upload.
const attendanceColumns: BulkUploadColumn[] = [
  { key: "employee_name", label: "Employee Name", required: true, aliases: ["employee_id", "name", "emp_name", "employee"] },
  { key: "date", label: "Date", required: true },
  { key: "status", label: "Status", required: true },
  { key: "leave_type", label: "Leave Type", aliases: ["leave_type_name", "leave"] },
  { key: "check_in", label: "Check In" },
  { key: "check_out", label: "Check Out" },
  { key: "notes", label: "Notes" },
];

const attendanceTemplate = `employee_name,date,status,leave_type,check_in,check_out,notes
Jane Doe,2026-02-01,present,,09:00:00,18:00:00,Regular day
John Smith,2026-02-01,late,,09:30:00,18:00:00,Late arrival
Akshata Doddamani,2026-02-01,leave,Sick Leave,,,On sick leave`;

// ─── Users & Roles (Combined) ─────────────────────
const usersAndRolesColumns: BulkUploadColumn[] = [
  { key: "email", label: "Email", required: true },
  { key: "full_name", label: "Full Name" },
  { key: "department", label: "Department" },
  { key: "job_title", label: "Job Title" },
  { key: "role", label: "Role", required: true },
];

const usersAndRolesTemplate = `email,full_name,department,job_title,role
john@grx10.com,John Doe,Engineering,Developer,employee
jane@grx10.com,Jane Smith,HR,HR Manager,hr
existing@grx10.com,,,,manager`;

// ─── Holidays ──────────────────────────────────────
const holidayColumns: BulkUploadColumn[] = [
  { key: "name", label: "Holiday Name", required: true },
  { key: "date", label: "Date (YYYY-MM-DD)", required: true },
];

const holidayTemplate = `name,date
Republic Day,2026-01-26
Independence Day,2026-08-15
Christmas,2026-12-25`;

// ─── Hook ──────────────────────────────────────────
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const formatPayPeriod = (p: string) => {
  const [y, m] = p.split("-");
  return `${MONTHS[parseInt(m) - 1]} ${y}`;
};

// ─── Payroll bulk upload ────────────────────────────
// Writes to payroll_runs + payroll_entries (engine path).
// Enables the full approval workflow (submit → HR review → finance → lock).
export function usePayrollRegisterBulkUpload(payPeriod: string): BulkUploadConfig {
  const { user } = useAuth();
  const qc = useQueryClient();

  const existingRecordCheck = useCallback(async (): Promise<{ message: string; canOverride: boolean } | null> => {
    if (!user) return null;
    const { data: profile } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user.id)
      .maybeSingle();
    const orgId = profile?.organization_id;
    if (!orgId) return null;

    const { data: run } = await supabase
      .from("payroll_runs")
      .select("id, status")
      .eq("organization_id", orgId)
      .eq("pay_period", payPeriod)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!run) return null;

    const terminalStatuses = ["under_review", "approved", "locked"];
    if (terminalStatuses.includes(run.status)) {
      return {
        message: `The payroll run for ${formatPayPeriod(payPeriod)} is ${run.status} and cannot be overwritten.`,
        canOverride: false,
      };
    }

    // head+count is cheap; run in parallel with nothing else needed here.
    const { count } = await supabase
      .from("payroll_entries")
      .select("id", { count: "exact", head: true })
      .eq("payroll_run_id", run.id);

    if (count && count > 0) {
      return {
        message: `${count} payroll entr${count === 1 ? "y" : "ies"} already exist for ${formatPayPeriod(payPeriod)}. Uploading will overwrite them. This cannot be undone.`,
        canOverride: true,
      };
    }
    return null;
  }, [user, payPeriod]);

  const onUpload = useCallback(async (
    rows: Record<string, string>[],
    onProgress?: (processed: number, total: number, label?: string) => void,
    signal?: AbortSignal,
  ) => {
    if (!user) throw new Error("Not authenticated");

    console.log("[PayrollUpload] start, rows=", rows.length);
    const t0 = Date.now();
    const { data: currentProfile, error: cpErr } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user.id)
      .maybeSingle();
    console.log("[PayrollUpload] currentProfile", { ms: Date.now() - t0, cpErr, currentProfile });

    const orgId = currentProfile?.organization_id;
    if (!orgId) return { success: 0, errors: ["No organization found on your profile. Contact admin."] };

    const t1 = Date.now();
    const { data: profiles, error: profilesErr } = await supabase
      .from("profiles")
      .select("id, user_id, email, full_name")
      .eq("organization_id", orgId)
      .in("status", ["active", "on_leave"]);
    console.log("[PayrollUpload] profiles list", { ms: Date.now() - t1, count: profiles?.length, profilesErr });

    const errors: string[] = [];
    const warnings: string[] = [];
    let success = 0;

    const findProfileByEmail = (email: string) => {
      if (!profiles || !email) return null;
      return profiles.find(p => p.email?.toLowerCase().trim() === email.toLowerCase().trim()) ?? null;
    };

    // Exact full_name match only — fuzzy tiers omitted for financial records (FMEA 2.1).
    const findProfileByName = (empId: string) => {
      if (!profiles || !empId) return null;
      const needle = empId.toLowerCase().trim();
      return profiles.find(p => p.full_name?.toLowerCase().trim() === needle) ?? null;
    };

    // ── Find or create payroll_run ─────────────────────────────────────────
    const t2 = Date.now();
    const { data: existingRun, error: existingRunErr } = await supabase
      .from("payroll_runs")
      .select("id, status")
      .eq("organization_id", orgId)
      .eq("pay_period", payPeriod)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    console.log("[PayrollUpload] existingRun", { ms: Date.now() - t2, existingRun, existingRunErr });

    const terminalStatuses = ["under_review", "approved", "locked"];
    if (existingRun && terminalStatuses.includes(existingRun.status)) {
      return {
        success: 0,
        errors: [
          `A payroll run for ${formatPayPeriod(payPeriod)} already exists with status '${existingRun.status}'. ` +
          `It cannot be overwritten. Delete it first or choose a different period.`,
        ],
      };
    }

    let runId: string;
    let createdNewRun = false;
    // Snapshot pre-state per profile so we can report what actually changed
    // after the upsert. Keyed by profile_id, holds the pre-upload totals.
    const beforeByProfile = new Map<string, { gross: number; deductions: number; net: number }>();

    if (existingRun) {
      runId = existingRun.id;
      const { data: priorEntries } = await supabase
        .from("payroll_entries")
        .select("profile_id, gross_earnings, total_deductions, net_pay")
        .eq("payroll_run_id", existingRun.id);
      for (const e of (priorEntries ?? []) as any[]) {
        beforeByProfile.set(e.profile_id, {
          gross: Number(e.gross_earnings) || 0,
          deductions: Number(e.total_deductions) || 0,
          net: Number(e.net_pay) || 0,
        });
      }
    } else {
      const t3 = Date.now();
      const { data: newRun, error: runError } = await supabase
        .from("payroll_runs")
        .insert({
          organization_id: orgId,
          pay_period: payPeriod,
          generated_by: user.id,
          status: "completed",
          notes: "Uploaded via Payroll Register Bulk Upload",
        })
        .select("id")
        .single();
      console.log("[PayrollUpload] insert payroll_run", { ms: Date.now() - t3, newRun, runError });

      if (runError || !newRun) {
        return { success: 0, errors: [`Failed to create payroll run: ${runError?.message ?? "unknown error"}`] };
      }
      runId = newRun.id;
      createdNewRun = true;
    }

    const insertedEntryIds: string[] = [];
    const failedRows: Array<{ rowIndex: number; data: Record<string, string>; error: string }> = [];

    const rollbackAll = async () => {
      if (insertedEntryIds.length > 0) {
        await supabase.from("payroll_entries").delete().in("id", insertedEntryIds);
      }
      if (createdNewRun) {
        await supabase.from("payroll_runs").delete().eq("id", runId);
      }
    };

    const abortOnThreshold = async () => {
      if (errors.length > rows.length * 0.5 && insertedEntryIds.length > 0) {
        await rollbackAll();
        return true;
      }
      return false;
    };

    // ── Pass 1: parse + validate every row, build payload list ────────────
    // Heavy work was originally a per-row `await supabase.upsert(...).select().single()`
    // which meant one round-trip per row. For 34 rows on a slow link the dialog
    // would visibly stall. We now parse all rows synchronously, collect errors,
    // and write valid rows in batches of 50 — typically 1-2 round-trips total.
    type PendingPayload = {
      rowIndex: number;
      raw: Record<string, string>;
      payload: Record<string, any>;
    };
    const pending: PendingPayload[] = [];

    rows.forEach((row, idx) => {
      const rowIndex = idx + 1;

      const pf_monthly       = parseFloat(row.pf_employee_monthly) || 0;
      const prof_tax         = parseFloat(row.professional_tax_monthly) || 0;
      const tds_monthly_raw  = parseFloat(row.tds_monthly) || 0;
      const other_ded_raw    = parseFloat(row.other_deductions_col) || 0;
      // File's Total Deductions includes LWP per the Bulk Edit template formula
      // (=PF+PT+TDS+Other+LWP). DB stores total_deductions in PF+PT+TDS+Other
      // shape with lwp_deduction separate — strip LWP before persisting.
      const total_ded_file_raw = parseFloat(row.total_deductions_col) || 0;
      const monthly_gross    = parseFloat(row.monthly_gross) || 0;
      const grossEarningsExplicit = parseFloat(row.gross_earnings_monthly);
      const hasExplicitGross = !isNaN(grossEarningsExplicit) && grossEarningsExplicit > 0;
      const incentive        = parseFloat(row.incentive_monthly) || 0;
      const bonus            = parseFloat(row.bonus_monthly) || 0;

      // Per-component earnings split (Bulk Edit path). When ANY of these is > 0
      // the breakdown is built component-by-component and Monthly Fixed Salary
      // is derived from their sum; otherwise we fall back to the legacy
      // single-line "Basic Salary = gross" behaviour.
      const basicExplicit      = parseFloat(row.basic_monthly) || 0;
      const hraExplicit        = parseFloat(row.hra_monthly) || 0;
      const transportExplicit  = parseFloat(row.transport_monthly) || 0;
      const otherAllowExplicit = parseFloat(row.other_allowances_monthly) || 0;
      const hasExplicitComponents = basicExplicit > 0 || hraExplicit > 0 || transportExplicit > 0 || otherAllowExplicit > 0;
      const fixedFromComponents = basicExplicit + hraExplicit + transportExplicit + otherAllowExplicit;
      const monthly_fixed = hasExplicitComponents
        ? fixedFromComponents
        : (hasExplicitGross ? grossEarningsExplicit : monthly_gross);

      const [yyU, mmU] = payPeriod.split("-").map(Number);
      const daysInMonthU = new Date(yyU, mmU, 0).getDate();
      const lwp_days_val     = parseFloat(row.lwp_days_col) || 0;
      const lwp_ded_val      = parseFloat(row.lwp_deduction_col) || 0;
      const working_days_val = parseFloat(row.working_days_col) || daysInMonthU;
      const paid_days_val    = parseFloat(row.paid_days_col)
        || Math.max(0, working_days_val - lwp_days_val);
      const net_from_file    = parseFloat(row.net_pay_file) || 0;

      const basic = hasExplicitComponents ? basicExplicit : Math.round(monthly_fixed);
      const componentSum = pf_monthly + prof_tax + tds_monthly_raw + other_ded_raw;
      const total_ded_file = total_ded_file_raw > 0
        ? Math.max(0, total_ded_file_raw - lwp_ded_val)
        : 0;
      const totalDed = total_ded_file > 0 ? total_ded_file : componentSum;
      const grossWithVariable = monthly_fixed + bonus + incentive;
      const net_pay = net_from_file > 0
        ? net_from_file
        : Math.max(0, Math.round(grossWithVariable - totalDed - lwp_ded_val));

      let profile;
      if (row.email_id?.trim()) {
        profile = findProfileByEmail(row.email_id.trim());
        if (!profile) {
          const err = `Row ${row.employee_id || row.email_id}: No employee found with email "${row.email_id.trim()}"`;
          errors.push(err);
          failedRows.push({ rowIndex, data: row, error: err });
          return;
        }
      } else {
        profile = findProfileByName(row.employee_id);
        if (!profile) {
          const err = `Row ${row.employee_id}: No matching employee profile found`;
          errors.push(err);
          failedRows.push({ rowIndex, data: row, error: err });
          return;
        }
      }

      const earningsBreakdown = [
        { name: "Basic Salary",      monthly: basic, annual: basic * 12, is_taxable: true },
        ...(bonus > 0     ? [{ name: "Bonus",     monthly: bonus,     annual: bonus * 12,     is_taxable: true }] : []),
        ...(incentive > 0 ? [{ name: "Incentive", monthly: incentive, annual: incentive * 12, is_taxable: true }] : []),
      ];
      // Absorb any gap between the file-provided total and component sum into
      // "Other Deductions" so the breakdown always reconciles with total_deductions
      // (CLAUDE.md invariant: components must not exceed total; gap → other_ded).
      const breakdownOtherDed = total_ded_file > 0 && total_ded_file > componentSum
        ? other_ded_raw + (total_ded_file - componentSum)
        : other_ded_raw;
      const deductionsBreakdown = [
        ...(pf_monthly > 0        ? [{ name: "PF Contribution",  monthly: pf_monthly,       annual: pf_monthly * 12,       is_taxable: false }] : []),
        ...(prof_tax > 0          ? [{ name: "Professional Tax", monthly: prof_tax,         annual: prof_tax * 12,         is_taxable: false }] : []),
        ...(tds_monthly_raw > 0   ? [{ name: "TDS",              monthly: tds_monthly_raw,  annual: tds_monthly_raw * 12,  is_taxable: false }] : []),
        ...(breakdownOtherDed > 0 ? [{ name: "Other Deductions", monthly: breakdownOtherDed, annual: breakdownOtherDed * 12, is_taxable: false }] : []),
      ];

      pending.push({
        rowIndex,
        raw: row,
        payload: {
          payroll_run_id:            runId,
          profile_id:                profile.id,
          organization_id:           orgId,
          compensation_structure_id: null,
          annual_ctc:                grossWithVariable * 12,
          gross_earnings:            grossWithVariable,
          total_deductions:          totalDed,
          net_pay,
          lwp_days:                  lwp_days_val,
          lwp_deduction:             lwp_ded_val,
          working_days:              working_days_val,
          paid_days:                 paid_days_val,
          earnings_breakdown:        earningsBreakdown,
          deductions_breakdown:      deductionsBreakdown,
          pf_employee:               pf_monthly,
          tds_amount:                tds_monthly_raw,
          status:                    "computed",
        },
      });
    });

    // Initial progress signal — parsing complete
    onProgress?.(0, rows.length, "Validating");

    // Threshold abort BEFORE we hit the network if half of the rows already
    // failed validation.
    if (errors.length > rows.length * 0.5) {
      await rollbackAll();
      return {
        success: 0,
        errors: [...errors, "Bulk upload aborted: too many invalid rows. All changes rolled back."],
        warnings,
        failedRows,
      };
    }

    // ── Pass 2: batch upserts ─────────────────────────────────────────────
    const BATCH_SIZE = 50;
    let processedCount = rows.length - pending.length; // skipped rows count as processed
    onProgress?.(processedCount, rows.length, "Saving");

    for (let i = 0; i < pending.length; i += BATCH_SIZE) {
      if (signal?.aborted) {
        await rollbackAll();
        return {
          success: 0,
          errors: ["Upload cancelled by user. All changes rolled back."],
          warnings,
          cancelled: true,
          failedRows,
        };
      }
      const slice = pending.slice(i, i + BATCH_SIZE);
      const { data: inserted, error: batchErr } = await supabase
        .from("payroll_entries")
        .upsert(slice.map((p) => p.payload) as any, { onConflict: "payroll_run_id,profile_id" })
        .select("id");

      if (batchErr) {
        // Whole batch failed — record one error per row in the batch so the
        // retry UI surfaces them, then check the abort threshold.
        slice.forEach((p) => {
          const err = `Row ${p.raw.employee_id || p.raw.email_id}: ${batchErr.message}`;
          errors.push(err);
          failedRows.push({ rowIndex: p.rowIndex, data: p.raw, error: err });
        });
        if (await abortOnThreshold()) {
          return { success: 0, errors: [...errors, "Bulk upload aborted: too many errors. All changes rolled back."], failedRows };
        }
      } else {
        if (inserted) {
          for (const r of inserted) if (r?.id) insertedEntryIds.push(r.id);
        }
        success += slice.length;
      }
      processedCount += slice.length;
      onProgress?.(processedCount, rows.length, "Saving");
    }

    // ── Re-aggregate run totals + build per-employee change report ────────
    const { data: allEntries } = await supabase
      .from("payroll_entries")
      .select("profile_id, gross_earnings, total_deductions, net_pay")
      .eq("payroll_run_id", runId);

    if (allEntries && allEntries.length > 0) {
      await supabase.from("payroll_runs").update({
        total_gross:       allEntries.reduce((s, e) => s + (e.gross_earnings || 0), 0),
        total_deductions:  allEntries.reduce((s, e) => s + (e.total_deductions || 0), 0),
        total_net:         allEntries.reduce((s, e) => s + (e.net_pay || 0), 0),
        employee_count:    allEntries.length,
        status:            "completed",
      }).eq("id", runId);
    }

    // Compute change report by comparing before/after totals per profile.
    const profileNameById = new Map<string, string>();
    for (const p of (profiles ?? []) as any[]) profileNameById.set(p.id, p.full_name || p.email || p.id);
    let createdCount = 0;
    let updatedCount = 0;
    let unchangedCount = 0;
    let deltaGross = 0;
    let deltaNet = 0;
    let deltaDeductions = 0;
    const changedNames: string[] = [];
    for (const after of (allEntries ?? []) as any[]) {
      const before = beforeByProfile.get(after.profile_id);
      const aGross = Number(after.gross_earnings) || 0;
      const aDed = Number(after.total_deductions) || 0;
      const aNet = Number(after.net_pay) || 0;
      if (!before) {
        createdCount++;
        deltaGross += aGross; deltaDeductions += aDed; deltaNet += aNet;
        const nm = profileNameById.get(after.profile_id);
        if (nm) changedNames.push(`${nm} (new)`);
        continue;
      }
      const dG = aGross - before.gross;
      const dD = aDed - before.deductions;
      const dN = aNet - before.net;
      if (Math.abs(dG) < 1 && Math.abs(dD) < 1 && Math.abs(dN) < 1) {
        unchangedCount++;
      } else {
        updatedCount++;
        deltaGross += dG; deltaDeductions += dD; deltaNet += dN;
        const nm = profileNameById.get(after.profile_id);
        if (nm) changedNames.push(`${nm} (Δnet ${dN >= 0 ? '+' : ''}${Math.round(dN)})`);
      }
    }
    const inr = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;
    warnings.push(
      `Report — ${updatedCount} updated · ${createdCount} created · ${unchangedCount} unchanged. ` +
      `Net total Δ ${deltaNet >= 0 ? '+' : ''}${inr(deltaNet)}, ` +
      `Gross Δ ${deltaGross >= 0 ? '+' : ''}${inr(deltaGross)}, ` +
      `Deductions Δ ${deltaDeductions >= 0 ? '+' : ''}${inr(deltaDeductions)}.`
    );
    if (changedNames.length > 0) {
      const shown = changedNames.slice(0, 10).join(", ");
      const more = changedNames.length > 10 ? ` (+${changedNames.length - 10} more)` : "";
      warnings.push(`Changed employees: ${shown}${more}`);
    }

    qc.invalidateQueries({ queryKey: ["payroll-runs"] });
    qc.invalidateQueries({ queryKey: ["payroll-entries"] });
    qc.invalidateQueries({ queryKey: ["payroll"] });
    return {
      success,
      errors,
      warnings,
      failedRows,
      created: createdCount,
      updated: updatedCount,
    };
  }, [user, payPeriod, qc]);

  return {
    module: "payroll_register",
    title: "Upload Payroll Register",
    description: `Upload a pre-computed payroll register for ${formatPayPeriod(payPeriod)}. Creates a completed payroll run ready for the approval workflow — no engine calculation needed.`,
    columns: payrollColumns,
    templateFileName: "payroll_register_template.csv",
    templateContent: payrollTemplate,
    onUpload,
    existingRecordCheck,
  };
}

// ─── Bulk Edit on the Payroll Register tab ──────────────────────────────────
// Wraps usePayrollRegisterBulkUpload with:
//   • templateBuilder that pre-fills the XLSX with the current run's 39 rows
//   • validateHeaders that rejects any added / removed / renamed columns
//   • validateRow that runs cross-field math checks at parse time so errors
//     show up the moment the file is selected (not after the user clicks
//     Upload and the engine fails mid-batch)
// Title says "Bulk Edit" instead of "Upload Register" to match the use case.
export function usePayrollBulkEdit(payPeriod: string): BulkUploadConfig {
  const base = usePayrollRegisterBulkUpload(payPeriod);
  const { user } = useAuth();

  const REQUIRED_HEADERS = [
    "Employee Name","Email ID","Department","Job Title",
    "Total Annual CTC","Annual CTC","Employer PF Annual","Bonus Yearly",
    "Incentive monthly","Bonus monthly",
    "Monthly fixed Salary","Basic Salary","HRA","Transport","Other Allowances",
    "Gross Earnings",
    "Profession Tax monthly","Employee PF deduction monthly","TDS",
    "Other Deductions","Total Deductions",
    "LWP Days","LWP Deduction",
    "Working Days","Paid Days","Net Pay",
  ];

  const validateHeaders = useCallback((rawHeaders: string[]): string[] => {
    const errs: string[] = [];
    const norm = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");
    const got = new Set(rawHeaders.map(norm));
    const expected = new Set(REQUIRED_HEADERS.map(norm));
    const missing = REQUIRED_HEADERS.filter((h) => !got.has(norm(h)));
    const extra = rawHeaders.filter((h) => h && !expected.has(norm(h)));
    if (missing.length > 0) errs.push(`Missing column(s): ${missing.join(", ")}. Re-download the form and edit values only.`);
    if (extra.length > 0)   errs.push(`Unknown column(s): ${extra.join(", ")}. Remove them and try again.`);
    return errs;
  }, []);

  const validateRow = useCallback((row: Record<string, string>): string[] => {
    const errs: string[] = [];
    const num = (k: string) => {
      const v = parseFloat(row[k]);
      return isNaN(v) ? 0 : v;
    };
    const basic = num("basic_monthly");
    const hra = num("hra_monthly");
    const transport = num("transport_monthly");
    const otherAllow = num("other_allowances_monthly");
    const monthlyFixed = num("monthly_gross");
    const grossFile = num("gross_earnings_monthly");
    const bonus = num("bonus_monthly");
    const incentive = num("incentive_monthly");
    const pf = num("pf_employee_monthly");
    const pt = num("professional_tax_monthly");
    const tds = num("tds_monthly");
    const otherDed = num("other_deductions_col");
    const totalDed = num("total_deductions_col");
    const lwpDays = num("lwp_days_col");
    const lwpDed = num("lwp_deduction_col");
    const workingDays = num("working_days_col");
    const paidDays = num("paid_days_col");
    const netPay = num("net_pay_file");

    const nonNeg = (label: string, v: number) => {
      if (v < 0) errs.push(`${label} cannot be negative (got ${v})`);
    };
    nonNeg("Basic Salary", basic);
    nonNeg("HRA", hra);
    nonNeg("Transport", transport);
    nonNeg("Other Allowances", otherAllow);
    nonNeg("Bonus monthly", bonus);
    nonNeg("Incentive monthly", incentive);
    nonNeg("Employee PF deduction monthly", pf);
    nonNeg("Profession Tax monthly", pt);
    nonNeg("TDS", tds);
    nonNeg("Other Deductions", otherDed);
    nonNeg("Total Deductions", totalDed);
    nonNeg("LWP Days", lwpDays);
    nonNeg("LWP Deduction", lwpDed);
    nonNeg("Working Days", workingDays);
    nonNeg("Paid Days", paidDays);
    nonNeg("Net Pay", netPay);

    if (lwpDays > workingDays && workingDays > 0) {
      errs.push(`LWP Days (${lwpDays}) cannot exceed Working Days (${workingDays})`);
    }
    // Math reconciliation — Excel formulas in the downloaded file keep these in
    // sync automatically while you edit. If they fail, the file was tampered
    // with outside Excel (or the formula evaluation was skipped).
    const componentSum = basic + hra + transport + otherAllow;
    if (componentSum > 0 && monthlyFixed > 0 && Math.abs(componentSum - monthlyFixed) > 1) {
      errs.push(`Monthly fixed Salary (${monthlyFixed}) must equal Basic + HRA + Transport + Other Allowances (${componentSum}). Open the file in Excel so the formula recalculates, then re-upload.`);
    }
    const expectedGross = (componentSum > 0 ? componentSum : monthlyFixed) + bonus + incentive;
    if (grossFile > 0 && Math.abs(grossFile - expectedGross) > 1) {
      errs.push(`Gross Earnings (${grossFile}) must equal Fixed + Bonus + Incentive (${expectedGross}). Open the file in Excel so the formula recalculates.`);
    }
    // Paid Days = Working Days − LWP Days
    if (workingDays > 0 && Math.abs(paidDays - (workingDays - lwpDays)) > 1) {
      errs.push(`Paid Days (${paidDays}) must equal Working Days − LWP Days (${workingDays - lwpDays}). Open the file in Excel so the formula recalculates.`);
    }
    // LWP Deduction = ROUND(Basic / WorkingDays * LWPDays, 0)
    if (basic > 0 && workingDays > 0) {
      const expectedLwpDed = Math.round((basic / workingDays) * lwpDays);
      if (Math.abs(lwpDed - expectedLwpDed) > 1) {
        errs.push(`LWP Deduction (${lwpDed}) must equal ROUND(Basic / Working Days × LWP Days, 0) = ${expectedLwpDed}. Open the file in Excel so the formula recalculates.`);
      }
    }
    // Total Deductions = PF + PT + TDS + Other + LWP Deduction (new bulk-edit convention)
    const dedSum = pf + pt + tds + otherDed + lwpDed;
    if (totalDed > 0 && dedSum > 0 && Math.abs(totalDed - dedSum) > 1) {
      errs.push(`Total Deductions (${totalDed}) must equal PF + PT + TDS + Other + LWP Deduction (${dedSum}). Open the file in Excel so the formula recalculates.`);
    }
    // Net Pay = Gross − Total Deductions (LWP is inside Total Deductions now)
    const expectedNet = expectedGross - (totalDed > 0 ? totalDed : dedSum);
    if (netPay > 0 && Math.abs(netPay - expectedNet) > 2) {
      errs.push(`Net Pay (${netPay}) must equal Gross − Total Deductions (${Math.round(expectedNet)}). Open the file in Excel so the formula recalculates.`);
    }
    return errs;
  }, []);

  const templateBuilder = useCallback(async () => {
    if (!user) throw new Error("Not authenticated");
    const { data: orgRow } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user.id)
      .maybeSingle();
    const orgId = (orgRow as any)?.organization_id;
    if (!orgId) throw new Error("Organization not found on your profile");
    const { data: runRow } = await supabase
      .from("payroll_runs")
      .select("id, pay_period")
      .eq("organization_id", orgId)
      .eq("pay_period", payPeriod)
      .maybeSingle();
    if (!runRow) throw new Error(`No payroll run exists for ${formatPayPeriod(payPeriod)} — generate one first.`);
    const { data: entries, error } = await supabase
      .from("payroll_entries")
      .select(
        "id, profile_id, gross_earnings, total_deductions, net_pay, annual_ctc, lwp_days, lwp_deduction, working_days, paid_days, earnings_breakdown, deductions_breakdown, pf_employee, pf_employer, tds_amount, profiles!profile_id(full_name, email, department, job_title, employee_id)"
      )
      .eq("payroll_run_id", (runRow as any).id);
    if (error) throw error;
    if (!entries || entries.length === 0) throw new Error("This run has no entries to export");

    const find = (arr: any[], ...needles: string[]) => {
      for (const n of needles) {
        const f = (arr || []).find((c: any) => c?.name?.toLowerCase().includes(n.toLowerCase()));
        if (f) return Number(f.monthly ?? 0);
      }
      return 0;
    };
    const HEADERS = REQUIRED_HEADERS;
    // Compute Excel column letters for the headers we need to reference in
    // formulas. Header order is fixed by REQUIRED_HEADERS so these are stable.
    const colLetter = (idx: number) => {
      let s = "";
      let n = idx;
      do {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      return s;
    };
    const colIdx = (label: string) => HEADERS.indexOf(label);
    const COL = {
      bonusMo:      colLetter(colIdx("Bonus monthly")),
      incMo:        colLetter(colIdx("Incentive monthly")),
      monthlyFix:   colLetter(colIdx("Monthly fixed Salary")),
      basic:        colLetter(colIdx("Basic Salary")),
      hra:          colLetter(colIdx("HRA")),
      transport:    colLetter(colIdx("Transport")),
      otherAllow:   colLetter(colIdx("Other Allowances")),
      gross:        colLetter(colIdx("Gross Earnings")),
      pt:           colLetter(colIdx("Profession Tax monthly")),
      pf:           colLetter(colIdx("Employee PF deduction monthly")),
      tds:          colLetter(colIdx("TDS")),
      otherDed:     colLetter(colIdx("Other Deductions")),
      totalDed:     colLetter(colIdx("Total Deductions")),
      lwpDays:      colLetter(colIdx("LWP Days")),
      lwpDed:       colLetter(colIdx("LWP Deduction")),
      workingDays:  colLetter(colIdx("Working Days")),
      net:          colLetter(colIdx("Net Pay")),
    };
    // Working days = calendar days in the payroll month (May → 31, Feb → 28/29).
    const [yy, mm] = payPeriod.split("-").map(Number);
    const daysInMonth = new Date(yy, mm, 0).getDate();
    const rows = entries.map((e: any, i: number) => {
      const r = i + 2; // data starts at row 2 (header is row 1)
      const p = e.profiles || {};
      const earnings = Array.isArray(e.earnings_breakdown) ? e.earnings_breakdown : [];
      const deductions = Array.isArray(e.deductions_breakdown) ? e.deductions_breakdown : [];
      const basic = find(earnings, "basic");
      const hra = find(earnings, "hra", "house rent");
      const transport = find(earnings, "transport", "conveyance");
      const incentive = find(earnings, "incentive");
      const bonus = find(earnings, "bonus");
      const pt = find(deductions, "professional tax");
      const tds = Number(e.tds_amount) || find(deductions, "tds");
      const pfEmp = Number(e.pf_employee) || 0;
      const totalDedDB = Number(e.total_deductions) || 0;
      const grossDB = Number(e.gross_earnings) || 0;
      // Whatever earnings DIDN'T match Basic/HRA/Transport get bucketed into
      // Other Allowances so the components always reconcile to gross.
      const sumOfMatched = basic + hra + transport + incentive + bonus;
      const otherAllow = Math.max(0, grossDB - sumOfMatched);
      // Other Deductions = whatever isn't PF/PT/TDS so totalDed reconciles.
      // DB.total_deductions already excludes LWP (LWP is in its own column).
      const otherDed = Math.max(0, totalDedDB - pfEmp - pt - tds);
      // Cells flagged with { f: "...", v: cached } are written as Excel
      // formulas WITH a cached numeric value. Editing any component cell in
      // Excel triggers a recompute (formula wins), and tools that don't
      // auto-evaluate formulas still see the right number via the cache.
      const f = (formula: string, cached: number) => ({ t: "n", f: formula, v: cached });
      const lwpDaysV       = Number(e.lwp_days) || 0;
      const workingDaysV   = daysInMonth;
      const lwpDedCached   = workingDaysV > 0 && lwpDaysV > 0
        ? Math.round((basic / workingDaysV) * lwpDaysV)
        : 0;
      const paidDaysCached = Math.max(0, workingDaysV - lwpDaysV);
      const fixedTotal     = basic + hra + transport + otherAllow;
      const grossTotal     = fixedTotal + bonus + incentive;
      const dedTotal       = pfEmp + pt + tds + otherDed + lwpDedCached;
      const netTotal       = grossTotal - dedTotal;
      return [
        p.full_name || "",
        p.email || "",
        p.department || "",
        p.job_title || "",
        Number(e.annual_ctc) || 0,
        Number(e.annual_ctc) || 0,
        (Number(e.pf_employer) || 0) * 12,
        "",
        incentive || 0,
        bonus || 0,
        f(`${COL.basic}${r}+${COL.hra}${r}+${COL.transport}${r}+${COL.otherAllow}${r}`, fixedTotal),
        basic,
        hra,
        transport,
        otherAllow,
        f(`${COL.monthlyFix}${r}+${COL.bonusMo}${r}+${COL.incMo}${r}`, grossTotal),
        pt,
        pfEmp,
        tds,
        otherDed,
        f(`${COL.pf}${r}+${COL.pt}${r}+${COL.tds}${r}+${COL.otherDed}${r}+${COL.lwpDed}${r}`, dedTotal),
        lwpDaysV,
        f(`ROUND(${COL.basic}${r}/${COL.workingDays}${r}*${COL.lwpDays}${r},0)`, lwpDedCached),
        workingDaysV,
        f(`${COL.workingDays}${r}-${COL.lwpDays}${r}`, paidDaysCached),
        f(`${COL.gross}${r}-${COL.totalDed}${r}`, netTotal),
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payroll Register");
    const arr = XLSX.write(wb, { bookType: "xlsx", type: "array", cellDates: false }) as ArrayBuffer;
    return {
      blob: new Blob([arr], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      filename: `payroll_register_${payPeriod}.xlsx`,
    };
  }, [user, payPeriod]);

  return {
    ...base,
    title: `Bulk Edit Payroll Register — ${formatPayPeriod(payPeriod)}`,
    description: `Download the form, edit values for any subset of employees, then re-upload. Column structure must match — no added or removed columns. All validation errors are shown immediately on file select.`,
    templateBuilder,
    templateFileName: `payroll_register_${payPeriod}.xlsx`,
    validateHeaders,
    validateRow,
  };
}

export function useAttendanceBulkUpload(): BulkUploadConfig {
  const { user, session } = useAuth();
  const qc = useQueryClient();
  // Hook-level orgId (needed by templateBuilder below). onUpload still does
  // its own profile lookup at submit time so it stays correct if the user
  // switches orgs mid-session.
  const { data: orgData } = useUserOrganization();
  const orgId = orgData?.organizationId;
  // The MS365 JWT lives in AuthContext's `session.access_token`. The
  // persistSessionSnapshot call meant to mirror it into localStorage
  // throws when VITE_SUPABASE_URL is empty (our prod build sets it empty
  // for same-origin requests), so localStorage is NOT a reliable source.
  const accessToken = (session as { access_token?: string } | null)?.access_token ?? null;

  const onUpload = useCallback(async (
    rows: Record<string, string>[],
    onProgress?: (processed: number, total: number, label?: string) => void,
  ) => {
    if (!user) throw new Error("Not authenticated");

    // Get the user's organization_id
    const { data: currentProfile } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user.id)
      .maybeSingle();

    const orgId = currentProfile?.organization_id;

    const { data: profiles } = await (orgId
      ? supabase.from("profiles").select("id, user_id, email, full_name").eq("organization_id", orgId).in("status", ["active", "on_leave"])
      : supabase.from("profiles").select("id, user_id, email, full_name").in("status", ["active", "on_leave"]));

    // Build leave_type name → id map for the row's org so we can convert
    // the dropdown's display label into the FK the DB expects. Match by
    // exact name first, then code, then case-insensitive name — keeps the
    // upload tolerant of users typing "sick leave" vs "Sick Leave".
    const { data: leaveTypes } = await (orgId
      ? supabase.from("leave_types").select("id, name, code").eq("organization_id", orgId)
      : supabase.from("leave_types").select("id, name, code"));
    const findLeaveTypeId = (raw: string): string | null => {
      const v = (raw || "").trim();
      if (!v || !leaveTypes) return null;
      const lower = v.toLowerCase();
      const m = leaveTypes.find((lt) => lt.name === v)
        || leaveTypes.find((lt) => lt.code === v)
        || leaveTypes.find((lt) => (lt.name || "").toLowerCase() === lower)
        || leaveTypes.find((lt) => (lt.code || "").toLowerCase() === lower);
      return m?.id || null;
    };

    const errors: string[] = [];
    let success = 0;

    /**
     * Flexible profile matching:
     * 1. Exact full_name match (case-insensitive)
     * 2. full_name starts with the employee_id value
     * 3. employee_id is contained in full_name (partial match)
     * 4. Email prefix match
     * This handles cases like "Akshata S Dod..." matching "Akshata S Doddamani"
     */
    const findProfile = (empId: string) => {
      if (!profiles || !empId) return null;
      const needle = empId.toLowerCase().trim();
      // 1. Exact full_name
      let match = profiles.find(p => p.full_name?.toLowerCase().trim() === needle);
      if (match) return match;
      // 2. full_name starts with employee_id
      match = profiles.find(p => p.full_name?.toLowerCase().startsWith(needle));
      if (match) return match;
      // 3. employee_id contained in full_name
      match = profiles.find(p => p.full_name?.toLowerCase().includes(needle));
      if (match) return match;
      // 4. Email prefix match
      match = profiles.find(p => p.email?.toLowerCase().startsWith(needle));
      if (match) return match;
      // 5. All words in employee_id appear in full_name (handles reordering / partial)
      const words = needle.split(/\s+/).filter(w => w.length > 1);
      if (words.length > 0) {
        match = profiles.find(p => {
          const name = p.full_name?.toLowerCase() || "";
          return words.every(w => name.includes(w));
        });
        if (match) return match;
      }
      return null;
    };

    for (const row of rows) {
      // Column was renamed employee_id → employee_name (see attendanceColumns).
      // Fall back to employee_id for older downloaded templates still in flight.
      const lookupKey = row.employee_name || row.employee_id || "";
      const profile = findProfile(lookupKey);

      if (!profile) {
        errors.push(`Row ${lookupKey}: No matching employee profile found`);
        onProgress?.(success + errors.length, rows.length);
        continue;
      }

      // user_id is NOT NULL — use the matched profile's user_id, or fall back to current user
      const resolvedUserId = profile.user_id || user.id;

      // Normalize time values — strip any date prefix, ensure HH:mm:ss format
      const normalizeTime = (t: string | undefined): string | null => {
        if (!t || !t.trim()) return null;
        const match = t.trim().match(/(\d{1,2}:\d{2}(:\d{2})?)/);
        if (match) return match[1].length === 5 ? match[1] + ":00" : match[1];
        return null;
      };

      const checkInTime = normalizeTime(row.check_in);
      const checkOutTime = normalizeTime(row.check_out);
      const checkInDate = checkInTime && row.date ? `${row.date}T${checkInTime}+05:30` : null;
      const checkOutDate = checkOutTime && row.date ? `${row.date}T${checkOutTime}+05:30` : null;

      console.log(`[Attendance Upload] ${lookupKey} | date=${row.date} | raw_in="${row.check_in}" → ${checkInTime} → ${checkInDate} | raw_out="${row.check_out}" → ${checkOutTime} → ${checkOutDate}`);

      const status = (row.status || "present").toLowerCase();
      const isLeaveStatus = status === "leave" || status === "half_day";
      const leaveTypeRaw = (row.leave_type || "").trim();
      let leaveTypeId: string | null = null;
      if (isLeaveStatus) {
        if (!leaveTypeRaw) {
          errors.push(`Row ${lookupKey} ${row.date}: status=${status} requires a leave_type — pick from the "leave_types" sheet`);
          onProgress?.(success + errors.length, rows.length);
          continue;
        }
        leaveTypeId = findLeaveTypeId(leaveTypeRaw);
        if (!leaveTypeId) {
          errors.push(`Row ${lookupKey} ${row.date}: Unknown leave_type "${leaveTypeRaw}" — pick a value from the "leave_types" sheet`);
          onProgress?.(success + errors.length, rows.length);
          continue;
        }
      }

      // Use upsert with profile_id+date conflict to handle re-uploads cleanly
      const { error } = await supabase.from("attendance_records")
        .upsert({
          user_id: resolvedUserId,
          profile_id: profile.id,
          date: row.date,
          status: row.status || "present",
          leave_type_id: leaveTypeId,
          check_in: checkInDate,
          check_out: checkOutDate,
          notes: row.notes || null,
          organization_id: orgId || null,
        }, {
          onConflict: "profile_id,date",
          ignoreDuplicates: false,
        });

      if (error) errors.push(`Row ${lookupKey} ${row.date}: ${error.message}`);
      else success++;
      onProgress?.(success + errors.length, rows.length);
    }

    qc.invalidateQueries({ queryKey: ["attendance"] });
    return { success, errors };
  }, [user, qc]);

  // XLSX template (2 sheets + dropdowns) is generated server-side. Earlier
  // attempt to build it in the browser via exceljs failed because exceljs
  // pulls Node-only modules that Vite can't bundle for the browser.
  // Backend has exceljs and full Node — clean fetch + download here.
  // Using a direct fetch (not invokeEdge) because invokeEdge always parses
  // the body as JSON; we need the raw binary XLSX blob.
  const templateBuilder = useCallback(async () => {
    if (!accessToken) {
      throw new Error("No active session — please sign in again");
    }
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
    const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const url = `${SUPABASE_URL}/functions/v1/attendance-template`;

    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON,
      },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`attendance-template endpoint returned ${res.status}: ${text.slice(0, 200)}`);
    }
    return {
      blob: await res.blob(),
      filename: "attendance_template.xlsx",
    };
  }, [accessToken]);

  return {
    module: "attendance",
    title: "Bulk Upload Attendance",
    description: "Upload attendance records for multiple employees and dates using a CSV/XLSX file.",
    columns: attendanceColumns,
    templateFileName: "attendance_template.csv",
    templateContent: attendanceTemplate,
    templateBuilder,
    onUpload,
  };
}

export function useHolidaysBulkUpload(): BulkUploadConfig {
  const qc = useQueryClient();
  const { data: orgData } = useUserOrganization();
  const orgId = orgData?.organizationId;

  const onUpload = useCallback(async (rows: Record<string, string>[]) => {
    const errors: string[] = [];
    let success = 0;

    if (!orgId) {
      return { success: 0, errors: ["No organization found. Please try again."] };
    }

    for (const row of rows) {
      const name = row.name?.trim();
      const date = row.date?.trim();
      if (!name || !date) {
        errors.push(`Missing name or date: "${name}", "${date}"`);
        continue;
      }
      const year = new Date(date).getFullYear();
      if (isNaN(year)) {
        errors.push(`Invalid date for "${name}": "${date}"`);
        continue;
      }

      const { error } = await supabase.from("holidays").insert({ name, date, year, organization_id: orgId });
      if (error) errors.push(`${name}: ${error.message}`);
      else success++;
    }

    qc.invalidateQueries({ queryKey: ["holidays"] });
    return { success, errors };
  }, [qc, orgId]);

  return {
    module: "holidays",
    title: "Bulk Upload Holidays",
    description: "Upload multiple holidays at once using a CSV or Excel file with name and date columns.",
    columns: holidayColumns,
    templateFileName: "holidays_template.csv",
    templateContent: holidayTemplate,
    onUpload,
  };
}

// ─── Expenses ──────────────────────────────────────
const expenseColumns: BulkUploadColumn[] = [
  { key: "employee_id", label: "Employee Name/Email", required: true },
  { key: "category", label: "Category", required: true },
  { key: "amount", label: "Amount", required: true },
  { key: "expense_date", label: "Date (YYYY-MM-DD)", required: true },
  { key: "description", label: "Description" },
  { key: "notes", label: "Notes" },
];

const expenseTemplate = `employee_id,category,amount,expense_date,description,notes
John Doe,Travel,5000,2026-03-01,Client visit to Mumbai,Cab + Hotel
Jane Smith,Office Supplies,1200,2026-03-02,Stationery purchase,`;

export function useExpensesBulkUpload(): BulkUploadConfig {
  const { user } = useAuth();
  const qc = useQueryClient();

  const onUpload = useCallback(async (rows: Record<string, string>[]) => {
    if (!user) throw new Error("Not authenticated");

    // Get the user's organization_id to scope profile lookups to current tenant
    const { data: currentProfile } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user.id)
      .maybeSingle();

    const orgId = currentProfile?.organization_id;

    // Fetch profiles scoped to current organization to prevent cross-tenant matches
    const { data: profiles } = await (orgId
      ? supabase.from("profiles").select("id, user_id, email, full_name, organization_id").eq("organization_id", orgId).in("status", ["active", "on_leave"])
      : supabase.from("profiles").select("id, user_id, email, full_name, organization_id").in("status", ["active", "on_leave"]));
    const errors: string[] = [];
    let success = 0;

    // Expenses are financial — exact match only (FMEA 2.1 adjacency).
    // Column label is "Employee Name/Email": if the value contains "@" treat
    // it as an email (exact match); otherwise exact full_name match.
    // Fuzzy tiers (startsWith / includes / word-split) are intentionally omitted.
    const findProfile = (empId: string) => {
      if (!profiles || !empId?.trim()) return null;
      const needle = empId.trim().toLowerCase();
      if (needle.includes("@")) {
        return profiles.find(p => p.email?.toLowerCase() === needle) ?? null;
      }
      return profiles.find(p => p.full_name?.toLowerCase().trim() === needle) ?? null;
    };

    for (const row of rows) {
      const profile = findProfile(row.employee_id);
      if (!profile) {
        errors.push(`Row ${row.employee_id}: No matching employee profile found`);
        continue;
      }

      const amount = parseFloat(row.amount);
      if (isNaN(amount) || amount <= 0) {
        errors.push(`Row ${row.employee_id}: Invalid amount "${row.amount}"`);
        continue;
      }

      const { error } = await supabase.from("expenses").insert({
        user_id: profile.user_id,
        profile_id: profile.id,
        organization_id: profile.organization_id,
        category: row.category?.trim() || "Miscellaneous",
        amount,
        expense_date: row.expense_date || new Date().toISOString().split("T")[0],
        description: row.description?.trim() || null,
        notes: row.notes?.trim() || null,
        status: "pending",
      });

      if (error) errors.push(`Row ${row.employee_id}: ${error.message}`);
      else success++;
    }

    qc.invalidateQueries({ queryKey: ["expenses-all"] });
    qc.invalidateQueries({ queryKey: ["expenses-my"] });
    return { success, errors };
  }, [user, qc]);

  return {
    module: "expenses",
    title: "Bulk Upload Expenses",
    description: "Upload multiple expense records at once. Employee matching uses name or email. Receipts can be attached individually after import.",
    columns: expenseColumns,
    templateFileName: "expenses_template.csv",
    templateContent: expenseTemplate,
    onUpload,
  };
}

export function useUsersAndRolesBulkUpload(): BulkUploadConfig {
  const qc = useQueryClient();

  const onUpload = useCallback(async (rows: Record<string, string>[]) => {
    const validRoles = ["admin", "hr", "manager", "employee", "finance"];
    const errors: string[] = [];
    let success = 0;
    let created = 0;
    let updated = 0;

    for (const row of rows) {
      const email = row.email?.trim();
      const role = row.role?.toLowerCase().trim() || "employee";
      const full_name = row.full_name?.trim() || "";
      const department = row.department?.trim() || null;
      const job_title = row.job_title?.trim() || null;

      if (!email) {
        errors.push(`Row missing email`);
        continue;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push(`Row "${email}": Invalid email format`);
        continue;
      }
      if (!validRoles.includes(role)) {
        errors.push(`${email}: Invalid role "${row.role}". Must be one of: ${validRoles.join(", ")}`);
        continue;
      }

      if (full_name) {
        const { data, error } = await supabase.functions.invoke("manage-roles", {
          body: { action: "bulk_create_users", users: [{ email, full_name, department, job_title, role }] },
        });
        if (error) {
          errors.push(`${email}: ${error.message}`);
        } else if (data?.errors?.length) {
          errors.push(...data.errors.map((e: string) => `${email}: ${e}`));
        } else {
          success++;
          if (data?.updated > 0) updated++;
          else created++;
        }
      } else {
        const { data, error } = await supabase.functions.invoke("manage-roles", {
          body: { action: "set_role_by_email", email, role },
        });
        if (error || data?.error) {
          errors.push(`${email}: ${data?.error || error?.message || "Failed"}`);
        } else {
          success++;
          updated++;
        }
      }
    }

    qc.invalidateQueries({ queryKey: ["user-roles"] });
      qc.invalidateQueries({ queryKey: ["session-context"] });
    return { success, errors, created, updated };
  }, [qc]);

  return {
    module: "users",
    title: "Bulk Add Users & Assign Roles",
    description: "Add new users or update roles for existing users. Provide full_name to create a new account, or leave it blank to just update the role of an existing user.",
    columns: usersAndRolesColumns,
    templateFileName: "users_roles_template.csv",
    templateContent: usersAndRolesTemplate,
    onUpload,
  };
}

// ─── Employees ─────────────────────────────────────
const employeeColumns: BulkUploadColumn[] = [
  { key: "full_name", label: "Full Name", required: true },
  { key: "email", label: "Email", required: true },
  { key: "job_title", label: "Job Title" },
  { key: "department", label: "Department" },
  { key: "status", label: "Status (active/inactive)" },
  { key: "join_date", label: "Join Date (YYYY-MM-DD)" },
  { key: "phone", label: "Phone" },
  { key: "manager", label: "Manager (Name or Email)" },
];

const employeeTemplate = `full_name,email,job_title,department,status,join_date,phone,manager
John Doe,john@company.com,Software Engineer,Engineering,active,2026-01-15,+91 98765 43210,manager@company.com
Jane Smith,jane@company.com,HR Manager,Human Resources,active,2026-02-01,+91 91234 56789,John Doe`;

export function useEmployeeBulkUpload(): BulkUploadConfig {
  const qc = useQueryClient();

  const onUpload = useCallback(async (rows: Record<string, string>[]) => {
    const errors: string[] = [];
    let success = 0;
    let created = 0;

    for (const row of rows) {
      const full_name = row.full_name?.trim();
      const email = row.email?.trim();

      if (!full_name || !email) {
        errors.push(`Row "${full_name || email || "?"}": Full name and email are required`);
        continue;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push(`Row "${full_name}": Invalid email format "${email}"`);
        continue;
      }

      const payload: Record<string, any> = {
        action: "create_user",
        full_name,
        email,
        role: "employee",
      };

      if (row.job_title?.trim()) payload.job_title = row.job_title.trim();
      if (row.department?.trim()) payload.department = row.department.trim();
      if (row.phone?.trim()) payload.phone = row.phone.trim();
      if (row.join_date?.trim()) payload.join_date = row.join_date.trim();
      if (row.status?.trim()) {
        const s = row.status.trim().toLowerCase();
        if (["active", "inactive"].includes(s)) payload.status = s;
      }
      // Accept manager as name or email — detect by presence of '@'
      const managerVal = (row.manager || row.manager_email || "").trim();
      if (managerVal) {
        if (managerVal.includes("@")) {
          payload.manager_email = managerVal;
        } else {
          // Treat as manager name — resolve to email via profiles lookup
          const { data: managerProfile } = await supabase
            .from("profiles")
            .select("email")
            .ilike("full_name", managerVal)
            .limit(1)
            .maybeSingle();
          if (managerProfile?.email) {
            payload.manager_email = managerProfile.email;
          } else {
            errors.push(`${email}: Manager "${managerVal}" not found`);
            continue;
          }
        }
      }

      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: payload,
      });

      if (error || data?.error) {
        errors.push(`${email}: ${data?.error || error?.message || "Failed to create"}`);
      } else {
        success++;
        created++;
      }
    }

    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    return { success, errors, created };
  }, [qc]);

  return {
    module: "employees",
    title: "Bulk Upload Employees",
    description: "Upload multiple employees at once. Each row creates a new user account. The template matches the Add Employee form fields.",
    columns: employeeColumns,
    templateFileName: "employees_template.csv",
    templateContent: employeeTemplate,
    onUpload,
  };
}

// ─── Employee Details (Update Existing) ────────────────
// Updates extended fields for existing employees keyed by email.
// Maps directly to the profiles + employee_details tables.
const employeeDetailsColumns: BulkUploadColumn[] = [
  {
    key: "email",
    label: "Email ID",
    required: true,
    aliases: ["email_id", "employee_email", "emp_email"],
  },
  {
    key: "full_name",
    label: "Employee Name",
    aliases: ["employees_name", "employee_name", "name", "emp_name"],
  },
  {
    key: "employee_id",
    label: "Employee ID",
    aliases: ["emp_id", "emp_code", "employee_code"],
  },
  {
    key: "join_date",
    label: "Date of Joining",
    aliases: ["date_of_joining", "doj", "joining_date", "date_joining"],
  },
  {
    key: "designation",
    label: "Designation",
    aliases: ["job_title", "position", "title", "role", "desination"],
  },
  {
    key: "department",
    label: "Department",
    aliases: ["dept", "team"],
  },
  {
    key: "gender",
    label: "Gender",
    aliases: ["sex"],
  },
  {
    key: "aadhar_no",
    label: "Aadhar Card No",
    aliases: ["aadhaar_no", "aadhar_number", "aadhaar_number", "aadhaar_card_no", "aadhar_card_no"],
  },
  {
    key: "pan_number",
    label: "Pan Card No",
    aliases: ["pan_no", "pan_card_no", "pan", "pan_card"],
  },
  {
    key: "date_of_birth",
    label: "Date of Birth",
    aliases: ["dob", "birth_date", "birthdate"],
  },
  {
    key: "uan",
    label: "UAN",
    aliases: ["uan_number", "uan_no", "universal_account_number"],
  },
  {
    key: "bank_account",
    label: "Bank Account",
    aliases: ["bank_account_no", "bank_account_number", "account_number", "account_no"],
  },
  {
    key: "ifsc_code",
    label: "IFSC Code",
    aliases: ["ifsc", "bank_ifsc", "ifsc_no"],
  },
  {
    key: "mobile_no",
    label: "Mobile No",
    aliases: ["phone", "mobile", "contact_number", "mobile_number", "phone_number"],
  },
  {
    key: "emergency_contact",
    label: "Emergency Contact",
    aliases: ["emergency_contact_name", "emergency_name", "emergency"],
  },
  {
    key: "permanent_address",
    label: "Permanent Address",
    aliases: ["address", "address_line1", "residence_address", "home_address"],
  },
  {
    key: "location",
    label: "Location",
    aliases: ["work_location", "office_location", "branch"],
  },
];

const employeeDetailsTemplate = `email_id,employees_name,employee_id,date_of_joining,designation,department,gender,aadhar_card_no,pan_card_no,date_of_birth,uan,bank_account,ifsc_code,mobile_no,emergency_contact,permanent_address,location
john@company.com,John Doe,EMP001,2024-01-15,Software Engineer,Engineering,Male,123456789012,ABCDE1234F,1990-05-20,123456789012,9876543210,SBIN0001234,+91 98765 43210,Jane Doe,123 Main St Mumbai,Mumbai`;

// Helper: normalise a date string to YYYY-MM-DD.
// Accepts YYYY-MM-DD or DD/MM/YYYY (common Indian Excel format).
function normaliseDateStr(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  // DD/MM/YYYY
  const dmy = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  return null;
}

export function useEmployeeDetailsBulkUpload(): BulkUploadConfig {
  const qc = useQueryClient();

  const onUpload = useCallback(async (rows: Record<string, string>[]) => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let success = 0;

    // Resolve the caller's org once — used for tenant isolation on every update.
    const { data: { user } } = await supabase.auth.getUser();
    const { data: callerProfile } = await supabase
      .from("profiles")
      .select("organization_id")
      .eq("user_id", user?.id ?? "")
      .maybeSingle();
    const callerOrgId = callerProfile?.organization_id ?? null;

    for (const row of rows) {
      const email = row.email?.trim();

      if (!email) {
        errors.push(`Row missing Email ID — skipped`);
        continue;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.push(`"${email}": Invalid email format — skipped`);
        continue;
      }

      // ── Field-level validation — invalid fields are skipped, not the row ──
      // Only email + employee-not-found are hard failures that skip the whole row.
      const skippedFields: string[] = [];

      // PAN — store as-is (no format enforcement)
      const pan: string | null = row.pan_number?.trim() ? row.pan_number.trim().toUpperCase() : null;

      // Aadhaar — accept full 12-digit, store only last 4
      let aadhaarLastFour: string | null = null;
      if (row.aadhar_no?.trim()) {
        const digits = row.aadhar_no.trim().replace(/[\s\-]/g, "");
        if (!/^\d{12}$/.test(digits)) {
          skippedFields.push(`Aadhaar skipped (must be 12 digits)`);
        } else {
          aadhaarLastFour = digits.slice(-4);
        }
      }

      // UAN — "NO"/"no" means explicitly not enrolled in EPF; blank means not provided (no change)
      let uan: string | null = null;
      if (row.uan?.trim()) {
        const rawUan = row.uan.trim();
        if (rawUan.toUpperCase() === "NO") {
          uan = "Opted out";
        } else {
          uan = rawUan.replace(/\s/g, "");
          if (!/^\d{12}$/.test(uan)) {
            skippedFields.push(`UAN skipped (must be 12 digits or "NO" for opted-out)`);
            uan = null;
          }
        }
      }

      // IFSC
      let ifsc: string | null = null;
      if (row.ifsc_code?.trim()) {
        ifsc = row.ifsc_code.trim().toUpperCase();
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
          skippedFields.push(`IFSC "${ifsc}" skipped (expected format: ABCD0123456)`);
          ifsc = null;
        }
      }

      // ── Resolve the profile by email (org-scoped via RLS) ─
      const { data: profile, error: profileLookupError } = await supabase
        .from("profiles")
        .select("id, organization_id")
        .ilike("email", email)
        .maybeSingle();

      if (profileLookupError) {
        errors.push(`"${email}": Lookup failed — ${profileLookupError.message}`);
        continue;
      }
      if (!profile) {
        errors.push(`"${email}": Employee not found — skipped`);
        continue;
      }
      // Explicit tenant check (defence-in-depth on top of RLS)
      if (callerOrgId && profile.organization_id !== callerOrgId) {
        errors.push(`"${email}": Employee belongs to a different organisation — skipped`);
        continue;
      }
      const profileId = profile.id;

      // ── Profile-level fields ──────────────────────────────
      // full_name is a generated column in DB — split into first/last instead.
      const profileUpdate: Record<string, string> = {};
      if (row.full_name?.trim()) {
        const trimmed = row.full_name.trim();
        const spaceIdx = trimmed.indexOf(" ");
        profileUpdate.first_name = spaceIdx > 0 ? trimmed.slice(0, spaceIdx) : trimmed;
        profileUpdate.last_name = spaceIdx > 0 ? trimmed.slice(spaceIdx + 1) : "";
        profileUpdate.display_name = trimmed;
      }
      if (row.designation?.trim()) profileUpdate.job_title = row.designation.trim();
      if (row.department?.trim()) profileUpdate.department = row.department.trim();
      if (row.mobile_no?.trim()) profileUpdate.phone = row.mobile_no.trim();
      if (row.location?.trim()) profileUpdate.location = row.location.trim();

      const joinDateNorm = normaliseDateStr(row.join_date || "");
      if (joinDateNorm) profileUpdate.join_date = joinDateNorm;

      if (Object.keys(profileUpdate).length > 0) {
        // No `.eq("organization_id", …)` filter: profiles has no organization_id
        // column on this schema (the adapter synthesizes it on SELECT via the
        // organization_members JOIN). Cross-org guard happens above against the
        // SELECT result; profileId itself is already org-scoped.
        const { error: profileUpdateError } = await supabase
          .from("profiles")
          .update(profileUpdate as any)
          .eq("id", profileId);
        if (profileUpdateError) {
          errors.push(`"${email}": Profile update failed — ${profileUpdateError.message}`);
          continue;
        }
      }

      // ── employee_details fields ───────────────────────────
      const detailsPayload: Record<string, string | null> = { profile_id: profileId };
      let hasDetails = false;

      if (row.employee_id?.trim()) { detailsPayload.employee_id_number = row.employee_id.trim(); hasDetails = true; }
      if (row.gender?.trim()) { detailsPayload.gender = row.gender.trim(); hasDetails = true; }
      if (row.emergency_contact?.trim()) { detailsPayload.emergency_contact_name = row.emergency_contact.trim(); hasDetails = true; }
      if (row.permanent_address?.trim()) { detailsPayload.address_line1 = row.permanent_address.trim(); hasDetails = true; }
      if (row.bank_account?.trim()) { detailsPayload.bank_account_number = row.bank_account.trim(); hasDetails = true; }

      const dobNorm = normaliseDateStr(row.date_of_birth || "");
      if (dobNorm) { detailsPayload.date_of_birth = dobNorm; hasDetails = true; }

      if (pan) { detailsPayload.pan_number = pan; hasDetails = true; }
      if (aadhaarLastFour) { detailsPayload.aadhaar_last_four = aadhaarLastFour; hasDetails = true; }
      if (uan) { detailsPayload.uan_number = uan; hasDetails = true; }
      if (ifsc) { detailsPayload.bank_ifsc = ifsc; hasDetails = true; }

      if (hasDetails) {
        const { error: detailsError } = await supabase
          .from("employee_details")
          .upsert(detailsPayload as any, { onConflict: "profile_id" });
        if (detailsError) {
          errors.push(`"${email}": Details update failed — ${detailsError.message}`);
          continue;
        }
      }

      success++;
      // Field-level skips go into warnings (row was saved, only these fields were not)
      if (skippedFields.length > 0) {
        warnings.push(`"${email}": ${skippedFields.join("; ")}`);
      }
    }

    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["employee-details"] });
    return { success, errors, warnings };
  }, [qc]);

  return {
    module: "employee-details",
    title: "Update Employee Details",
    description:
      "Update extended details for existing employees. Email ID is used as the primary key to match records. Aadhaar: only the last 4 digits are stored.",
    columns: employeeDetailsColumns,
    templateFileName: "employee_details_template.csv",
    templateContent: employeeDetailsTemplate,
    onUpload,
  };
}
